import { axiosInstance } from './axiosConfig';
import { handleRequestWithAuth } from './refreshService';

const SECURITY_LOGS_URL = '/securitylogs/';

export interface LogUser {
    first_name: string;
    last_name: string;
    email: string;
}

export interface SecurityLog {
    id: number;
    user: LogUser | null;
    action: string;
    ip_address: string | null;
    resultado: string;
    fecha: string;
}

export interface SecurityLogFilters {
    usuario?: string;
    accion?: string;
    ip?: string;
    resultado?: string;
    fecha_desde?: string;
    fecha_hasta?: string;
    page?: number;
    page_size?: number;
}

export interface SecurityLogPage {
    results: SecurityLog[];
    count: number;
    paginated: boolean;
}

const cleanParams = (filters: SecurityLogFilters) =>
    Object.fromEntries(
        Object.entries(filters).flatMap(([key, rawValue]) => {
            const value = typeof rawValue === 'string' ? rawValue.trim() : rawValue;
            return value === '' || value === undefined || value === null ? [] : [[key, value]];
        }),
    );

const isLogUser = (value: unknown): value is LogUser => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const user = value as Record<string, unknown>;
    return typeof user.first_name === 'string'
        && typeof user.last_name === 'string'
        && typeof user.email === 'string';
};

const isSecurityLog = (value: unknown): value is SecurityLog => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const log = value as Record<string, unknown>;
    return Number.isInteger(log.id)
        && (log.user === null || isLogUser(log.user))
        && typeof log.action === 'string'
        && (log.ip_address === null || typeof log.ip_address === 'string')
        && typeof log.resultado === 'string'
        && typeof log.fecha === 'string'
        && Number.isFinite(Date.parse(log.fecha));
};

const normalizeResponse = (data: SecurityLog[] | { results?: SecurityLog[]; count?: number }): SecurityLogPage => {
    const invalid = () => new Error('Respuesta inválida del servicio de seguridad.');
    if (Array.isArray(data)) {
        if (!data.every(isSecurityLog)) throw invalid();
        return { results: data, count: data.length, paginated: false };
    }
    if (!data || !Array.isArray(data.results) || !data.results.every(isSecurityLog)
        || !Number.isInteger(data.count) || (data.count as number) < 0) {
        throw invalid();
    }
    return { results: data.results, count: data.count as number, paginated: true };
};

export const getSecurityLogs = async (filters: SecurityLogFilters = {}): Promise<SecurityLogPage> =>
    handleRequestWithAuth(async () => {
        const response = await axiosInstance.get(SECURITY_LOGS_URL, { params: cleanParams(filters) });
        return normalizeResponse(response.data);
    });

